<template>
  <div :class="{ 'blur-background': isMapShown }">
    <Card class="cardClass">
      <Toolbar
        class="subheading toolbar-with-z-index"
        v-click-outside="closeSearchBarHandler"
      >
        <h4>Plant List</h4>
        <v-spacer></v-spacer>
        <TextInput
          :searchQuery="search"
          @search-submit="searchSubmitHandler"
          @filter-change="filterOptionChangeHandler"
          @search-input-change="searchChangeHandler"
        />
        <v-btn
          class="mapButton"
          @click="showMapHandler"
          v-show="$vuetify.breakpoint.mdAndUp"
        >
          {{ $t("View map") }}
          <img src="@/assets/locationicon.svg" alt="map" class="location" />
        </v-btn>
        <div class="legend-background">
          <v-btn class="home--datatable_legend" color="error" fab small />
          <span>{{ $t("New alert(s)") }}</span>
          <v-btn class="home--datatable_legend" color="success" fab small />
          <span>{{ $t("No alert(s)") }}</span>
        </div>
        <AvatarButton
          icon="settings"
          :tip="$t('Column settings')"
          class="hidden-sm-and-down"
          @click="openSettings"
        />
        <AvatarButton
          icon="get_app"
          :tip="$t('Download Excel')"
          class="hidden-sm-and-down"
          @click="download"
        />
      </Toolbar>
    </Card>
    <Card class="mt-1">
      <MobileList :data="data" v-if="isMobile" @alert-action="redirectAlert" />
      <DataTable
        v-else
        :headers="header"
        :data="data"
        :loading="dataLoading"
        :pagination.sync="pagination"
        @row-clicked="selectPlant"
        @alert-action="redirectAlert"
        @update-col-width="handleUpdateColWidth"
      />
    </Card>
    <v-divider></v-divider>
    <div class="text-xs-center pt-2">
      <v-layout class="paginationBox" justify-end>
        <v-flex shrink class="d-flex align-center pt-0 pb-0">
          <span class="primaryText--text mr-2">
            {{ itemFrom }} - {{ itemTo }} of {{ getTotalPlantCount }}
          </span>
          <span class="action d-flex align-center">
            <v-btn
              avatar
              fab
              small
              :disabled="!hasPrevious"
              class="fab-btn-xs mr-1"
              @click="previous"
            >
              <v-icon>navigate_before</v-icon>
            </v-btn>
            
            <div class="page-select-wrapper mx-2">
              <v-autocomplete
                v-model="page"
                :items="pageOptions"
                hide-details
                solo
                flat
                single-line
                placeholder="Page"
                class="pagination-select ma-0 pa-0"
                :menu-props="{ offsetY: true, auto: true }"
              ></v-autocomplete>
            </div>

            <v-btn
              avatar
              fab
              small
              :disabled="!hasNext"
              class="fab-btn-xs"
              @click="next"
            >
              <v-icon>navigate_next</v-icon>
            </v-btn>
          </span>
        </v-flex>
      </v-layout>
    </div>
    <v-dialog v-model="showSettings" persistent max-width="700px">
      <v-card color="primary">
        <v-card-title class="headline">
          {{ $t("Preferences") }}
          <v-spacer></v-spacer>
          <v-btn icon @click="showSettings = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs12 sm5 class="pr-3">
              <div class="subtitle-2 mb-2 font-weight-bold">
                {{ $t("Page size") }}
              </div>
              <v-radio-group
                v-model="tempPageSize"
                column
                hide-details
                class="mt-0 pt-0"
              >
                <v-radio :label="'15 resources'" :value="15"></v-radio>
                <v-radio :label="'30 resources'" :value="30"></v-radio>
                <v-radio :label="'50 resources'" :value="50"></v-radio>
              </v-radio-group>
            </v-flex>

            <v-flex
              xs12
              sm7
              class="pl-3"
              style="border-left: 1px solid rgba(128, 128, 128, 0.2)"
            >
              <div class="subtitle-2 font-weight-bold">
                {{ $t("Column preferences") }}
              </div>
              <div class="caption text--secondary mb-3">
                {{ $t("Customize the visibility and order of the columns.") }}
              </div>

              <draggable
                v-model="editColumnSettings"
                handle=".drag-handle"
                class="column-list"
              >
                <div
                  v-for="(col, index) in editColumnSettings"
                  :key="col.value"
                >
                  <v-layout align-center row class="py-1 m-0">
                    <v-icon class="drag-handle mx-3" style="cursor: grab" small
                      >drag_indicator</v-icon
                    >
                    <div class="text-xs-left">{{ $t(col.textKey) }}</div>
                    <v-spacer></v-spacer>
                    <v-switch
                      :disabled="col.value === 'plant_name'"
                      v-model="col.visible"
                      hide-details
                      class="mt-0 pt-0"
                      color="accent"
                      style="flex: 0 0 auto"
                    ></v-switch>
                  </v-layout>
                  <v-divider
                    v-if="index < editColumnSettings.length - 1"
                  ></v-divider>
                </div>
              </draggable>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn
            color="accent"
            @click="applySettings"
            class="text-none"
            :dark="true"
            >{{ $t("Confirm") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-tooltip left v-if="isSettingsDirty">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          v-on="on"
          fab
          fixed
          bottom
          right
          color="accent"
          class="mb-12 mr-2"
          @click="persistSettingsToDB"
        >
          <v-icon>save</v-icon>
        </v-btn>
      </template>
      <span>{{ $t("Save Table Layout") }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import moment from "moment";
import draggable from "vuedraggable";
import ExcelJS from "exceljs";
import { mapGetters, mapMutations, mapActions } from "vuex";
import { mixin, mapGettersMixin } from "@/util/mixin/index";
import TextInput from "@/components/home/TextInput.vue";
import AvatarButton from "@/components/home/AvatarButton.vue";
import MobileList from "@/components/home/DataTable/MobileList.vue";
import DataTable from "@/components/home/DataTable/Table.vue";
import MapComponent from "@/components/home/MapComponent.vue";

export default {
  mixins: [mixin, mapGettersMixin],
  props: {
    isMapShown: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TextInput,
    AvatarButton,
    MobileList,
    DataTable,
    MapComponent,
    draggable,
  },
  data() {
    let savedCols = this.$store.getters.getPlantDatatableCols;
    let savedSize = this.$store.getters.getPlantDatatableSize;
    let savedSortBy = this.$store.getters.getPlantDatatableSortBy;
    let savedDescending = this.$store.getters.getPlantDatatableDescending;
    return {
      pagination: {
        rowsPerPage: savedSize ? parseInt(savedSize) : 15,
        sortBy: savedSortBy || "plant_name",
        descending: savedDescending || false,
      },
      showSettings: false,
      tempPageSize: savedSize ? parseInt(savedSize) : 15,
      editColumnSettings: [],
      columnSettings: savedCols
        ? JSON.parse(JSON.stringify(savedCols))
        : [
            { value: "plant_name", visible: true, textKey: "Plant name" },
            {
              value: "location",
              visible: true,
              textKey: "City/ State/ Country",
            },
            { value: "generation", visible: true, textKey: "Energy (kWh)" },
            { value: "power", visible: true, textKey: "AC Power (kW)" },
            { value: "acyield", visible: true, textKey: "AC Yield (kWh)" },
            { value: "cuf", visible: true, textKey: "CUF" },
            { value: "ratio", visible: true, textKey: "PR (%)" },
            { value: "tempCorrPr", visible: true, textKey: "Temp Corr PR (%)" },
            { value: "albedoCorrPr", visible: true, textKey: "Albedo Corr PR (%)" },
            {
              value: "ac_capacity",
              visible: true,
              textKey: "AC Capacity (kW)",
            },
            { value: "alert", visible: true, textKey: "Status" }
          ],
      filter: "",
      search: "",
      page: 1,
      data: [],
      param: {
        plantList: [],
      },
      dataLoading: true,
      timer: "",
      fetchGeneration: 0,
      plantSearched: [],
      isSettingsDirty: false,
    };
  },
  mounted() {
    this.setIntervalWithoutDelay(this.callAllApi);
  },
  computed: {
    ...mapGetters([
      "getUserInfo",
      "getTotalPlantCount",
      "getPlantList",
      "getRules",
      "getSelectedPlantId",
      "isPlantExpired",
    ]),
    noOfPage() {
      return Math.ceil(this.getTotalPlantCount / this.pagination.rowsPerPage);
    },
    hasPagination() {
      return this.noOfPage > 1;
    },
    plantListForCurrentPage() {
      const startIndex = this.pagination.rowsPerPage * (this.page - 1);
      const endIndex = startIndex + this.pagination.rowsPerPage;
      let plantList = [];
      if (this.search === "") {
        plantList = this.getPlantList;
      } else {
        plantList = this.plantSearched;
      }

      return plantList.slice(startIndex, endIndex).map((el) => el.id);
    },
    isMobile() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
        case "sm":
          return true;
        case "md":
        case "lg":
        case "xl":
          return false;
      }
    },
    header() {
      const fullHeaders = {
        plant_name: { align: "left", width: "300px", class: "primary" },
        location: { class: "primary" },
        generation: { align: "left", class: "primary header-text" },
        power: { align: "left", class: "primary" },
        acyield: { align: "left", class: "primary" },
        cuf: { align: "left", class: "primary" },
        ratio: { align: "left", class: "primary" },
        albedoCorrPr: { align: "left", class: "primary" },
        tempCorrPr: { align: "left", class: "primary" },
        ac_capacity: { align: "left", class: "primary" },
        alert: { sortable: false, align: "left", class: "primary" },
      };

      return this.columnSettings
        .filter((col) => col.visible)
        .map((col) => ({
          ...fullHeaders[col.value],
          text: this.$t(col.textKey),
          value: col.value,
          ...(col.width && { width: col.width + "px" }),
        }));
    },
    hasPrevious() {
      return this.page > 1;
    },
    hasNext() {
      return this.page < this.noOfPage;
    },
    itemFrom() {
      return (this.page - 1) * this.pagination.rowsPerPage + 1;
    },
    itemTo() {
      return Math.min(
        this.page * this.pagination.rowsPerPage,
        this.getTotalPlantCount
      );
    },
    pageOptions() {
      return Array.from({ length: this.noOfPage }, (_, i) => i + 1);
    },
  },
  methods: {
    ...mapActions([
      "fetchGenerationData",
      "fetchActivePlantDetails",
      "saveTableConfig",
    ]),
    ...mapMutations([
      "SET_SELECTED_COMPONENT_MENU",
      "SET_LAST_CALL_TIME",
      "SET_PROGRESS_BAR",
      "SET_PLANT_DATATABLE_COLS",
      "SET_PLANT_DATATABLE_SIZE",
      "SET_PLANT_DATATABLE_SORT_BY",
      "SET_PLANT_DATATABLE_DESCENDING",
    ]),
    handleUpdateColWidth({ value, width }) {
      const idx = this.columnSettings.findIndex((c) => c.value === value);
      if (idx !== -1) {
        const updated = [...this.columnSettings];
        updated[idx].width = width;
        this.columnSettings = updated;
        this.SET_PLANT_DATATABLE_COLS(this.columnSettings);
        this.isSettingsDirty = true;
      }
    },
    openSettings() {
      this.tempPageSize = this.pagination.rowsPerPage;
      this.editColumnSettings = JSON.parse(JSON.stringify(this.columnSettings));
      this.showSettings = true;
    },
    applySettings() {
      const isColsChanged = !_.isEqual(
        this.columnSettings,
        this.editColumnSettings
      );
      const isPageSizeChanged =
        this.pagination.rowsPerPage !== this.tempPageSize;

      if (!isColsChanged && !isPageSizeChanged) {
        this.showSettings = false;
        return;
      }

      this.pagination.rowsPerPage = this.tempPageSize;
      this.columnSettings = JSON.parse(JSON.stringify(this.editColumnSettings));
      this.SET_PLANT_DATATABLE_COLS(this.columnSettings);
      this.SET_PLANT_DATATABLE_SIZE(this.pagination.rowsPerPage);
      this.showSettings = false;
      this.page = 1;

      this.isSettingsDirty = true;
      this.persistSettingsToDB();

      if (isPageSizeChanged) {
        this.callAllApi();
      }
    },
    persistSettingsToDB() {
      if (!this.isSettingsDirty) return;

      this.saveTableConfig({
        userId: this.getUserInfo("userId"),
        details: {
          ui_config: {
            home_table: {
              column: this.columnSettings,
              page_size: this.pagination.rowsPerPage,
              sort_by: this.pagination.sortBy,
              descending: this.pagination.descending,
            },
          },
        },
      });
      this.isSettingsDirty = false;
    },
    closeSearchBarHandler() {
      this.isExpanded = !this.isExpanded;
    },
    searchSubmitHandler(payload) {
      this.searchChangeHandler(payload.query);
      this.filterOptionChangeHandler(payload.filter);
      this.filterPlantHandler();
    },
    filterOptionChangeHandler(payload) {
      this.filter = payload;
    },
    searchChangeHandler(payload) {
      this.search = payload;
      this.filterPlantHandler();
    },
    filterPlantHandler() {
      const searchLower = this.search.toLowerCase();
      this.plantSearched = this.getPlantList.filter((el) => {
        if (this.filter == "plant") {
          return el.name.toLowerCase().includes(searchLower);
        } else if (this.filter == "location") {
          return el.location.toLowerCase().includes(searchLower);
        } else {
          return (
            el.name.toLowerCase().includes(searchLower) ||
            el.location.toLowerCase().includes(searchLower)
          );
        }
      });
    },
    async debounce() {
      await this.todayGenerationDetail();
    },
    showMapHandler() {
      this.$emit("isMapShownStatus", true);
    },
    callAllApi() {
      let updateTime = moment().format("MM-DD-YYYY H:mm:ss");
      this.SET_LAST_CALL_TIME(updateTime);
      this.todayGenerationDetail();
    },
    async redirectAlert({ plantId }) {
      await this.fetchActivePlantDetails(plantId);
      this.$router.push({ name: "alert" });
    },
    todayGenerationDetail: async function () {
      if (this.plantListForCurrentPage.length === 0) {
        this.data.splice(0);
        return;
      }

      const currentGeneration = ++this.fetchGeneration;

      const activePlants = this.plantListForCurrentPage
        .filter(id => !this.isPlantExpired(id));
      const expiredPlantIds = this.plantListForCurrentPage
        .filter(id => this.isPlantExpired(id));

      const capitalize = (str) =>
        str ? str.charAt(0).toUpperCase() + str.slice(1) : "N/A";
      const plantData = this.$store.state.plantData;
      const expiredRows = expiredPlantIds.map(id => {
        const pd = plantData[id] || {};
        return {
          plant_id: id,
          plant_name: pd.plant_name || "N/A",
          location: `${capitalize(pd.city)} / ${capitalize(pd.state)} / ${capitalize(pd.country)}`,
          ac_capacity: pd.ac_capacity || 0,
          generation: null,
          power: null,
          acyield: null,
          cuf: null,
          ratio: null,
          tempCorrPr: null,
          albedoCorrPr: null,
          alert: null,
        };
      });

      // If no active plants, show only expired placeholder rows
      if (activePlants.length === 0) {
        this.data = expiredRows;
        this.dataLoading = false;
        this.SET_PROGRESS_BAR(false);
        return;
      }

      this.dataLoading = true;
      this.fetchGenerationData({
        plantList: activePlants,
        visibleColumns: this.columnSettings
          .filter(c => c.visible)
          .map(c => c.value),
      })
        .then((response) => {
          if (currentGeneration !== this.fetchGeneration) return;

          this.dataLoading = false;
          if (response.status == 200) {
            const activeRows = response.data.message.map((item) => {
              return {
                ...item,
                location: `${capitalize(item.city)} / ${capitalize(
                  item.location
                )} / ${capitalize(item.country)}`,
              };
            });
            this.data = [...activeRows, ...expiredRows];
          }
        })
        .catch((err) => {
          if (currentGeneration !== this.fetchGeneration) return;
          this.dataLoading = false;
        });
    },
    refresh() {
      this.data.length = 0;
      this.setIntervalWithoutDelay(this.callAllApi);
    },
    async selectPlant(item) {
      const plantId = item.plant_id;
      await this.fetchActivePlantDetails(plantId);
      this.SET_SELECTED_COMPONENT_MENU("plantOverview");
      const path = this.getRules(["rule", "access_config"])
        ? this.getRules(["rule", "access_config"])["plantOverview"].path
        : "/plantOverview";
      this.$router.push(path);
    },
    async download() {
      const dataCopy = _.cloneDeep(this.data);
      if (dataCopy.length === 0) return;

      if (this.pagination.sortBy) {
        this.applySort(
          dataCopy,
          this.pagination.sortBy,
          this.pagination.descending
        );
      }

      this.SET_PROGRESS_BAR(true);
      const headers = this.header;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Plant Report");
      worksheet.views = [{ showGridLines: false }];

      try {
        const logoAsset = require("../../assets/logo/enerman-etisol_new.png");
        const response = await fetch(logoAsset);
        const arrayBuffer = await response.arrayBuffer();
        const imageId = workbook.addImage({
          buffer: arrayBuffer,
          extension: "png",
        });
        worksheet.addImage(imageId, {
          tl: { col: headers.length - 1, row: 0 },
          ext: { width: 150, height: 45 },
        });
      } catch (e) {
        console.error("Could not load logo for Excel", e);
      }

      const titleRow = worksheet.getRow(1);
      titleRow.values = ["ETi-SOL - Plant Report"];
      titleRow.getCell(1).font = { size: 16, bold: true };

      worksheet.addRow(["Date:", moment().format("DD-MM-YYYY")]);
      worksheet.addRow(["System Time:", moment().format("hh:mm A")]);
      worksheet.addRow([]);

      const headerRow = worksheet.addRow(headers.map((h) => h.text));
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF000427" },
        };
        cell.font = {
          color: { argb: "FFFFFFFF" },
          bold: true,
        };
        cell.alignment = { horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      dataCopy.forEach((item) => {
        const isExpired = this.isPlantExpired(item.plant_id);
        const rowValues = headers.map((h) => {
          let val = item[h.value];

          if (
            isExpired &&
            !["plant_name", "location", "ac_capacity"].includes(h.value)
          ) {
            val = "";
          } else if (h.value === "alert") {
            if (item.alert) {
              const alertMapper = {
                grid: "Grid",
                communication: "Comm",
                inverter: "Inverter",
                scb: "SCB",
                smb: "SMB",
                zmu: "ZMU",
                transformer: "Transformer",
                weather_station: "WS",
                ws: "WS",
                computed: "PR"
              };
              const active = Object.keys(item.alert)
                .filter(key => item.alert[key] === "alert")
                .map(key => alertMapper[key] || key.charAt(0).toUpperCase() + key.slice(1));
              
              val = active.join(", ") || "No Alert";
            } else {
              val = "NA";
            }
          } else if (h.value === "ratio") {
            val =
              item.ratio >= 100
                ? "--"
                : item.ratio == null
                ? "No data"
                : item.ratio;
          }
          return val !== undefined && val !== null ? val : "";
        });

        const row = worksheet.addRow(rowValues);

        if (isExpired) {
          row.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFBABA" },
            };
          });
        }

        const plantNameCell = row.getCell(1);
        plantNameCell.font = { bold: true };

        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      worksheet.columns = headers.map((h) => ({
        width: (parseInt(h.width) || 150) / 7.5,
      }));

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Plant_Report_${moment().format(
        "YYYY-MM-DD_HHmm"
      )}.xlsx`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      this.SET_PROGRESS_BAR(false);
    },
    applySort(items, index, isDesc) {
      const getNum = (v) => parseFloat((v || 0).toString().replace(/,/g, "")) || 0;

      const compare = (a, b) => {
        let valA, valB;

        if (["generation", "power", "acyield", "cuf", "ac_capacity","albedoCorrPr","tempCorrPr"].includes(index)) {
          valA = getNum(a[index]);
          valB = getNum(b[index]);
        } else if (index === "ratio") {
          if (typeof a[index] !== typeof b[index]) {
            return isDesc 
              ? (typeof a[index] > typeof b[index] ? -1 : 1) 
              : (typeof a[index] < typeof b[index] ? -1 : 1);
          }
          valA = a[index];
          valB = b[index];
        } else {
          valA = a[index] || "";
          valB = b[index] || "";
        }

        if (valA < valB) return isDesc ? 1 : -1;
        if (valA > valB) return isDesc ? -1 : 1;
        return 0;
      };

      items.sort(compare);
    },
    previous() {
      if (this.page > 1) this.page -= 1;
    },
    next() {
      if (this.page < this.noOfPage) this.page += 1;
    },
  },
  watch: {
    'pagination.sortBy'(newVal) {
      this.SET_PLANT_DATATABLE_SORT_BY(newVal);
      this.isSettingsDirty = true;
    },
    'pagination.descending'(newVal) {
      this.SET_PLANT_DATATABLE_DESCENDING(newVal);
      this.isSettingsDirty = true;
    },
    isReload() {
      if (this.isReload) {
        this.setIntervalWithoutDelay(this.callAllApi);
      }
    },
    page() {
      this.todayGenerationDetail();
    },
    search() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.page = 1;
        this.debounce();
      }, 1500);
    },
  },
};
</script>

<style scoped>
.toolbar-with-z-index {
  position: relative;
  z-index: 4;
}
.home--datatable_legend {
  height: 12px !important;
  width: 12px !important;
}

.cardClass {
  border-radius: 10px 10px 0px 0px;
}

.fab-btn-xs {
  height: 20px;
  width: 20px;
}

.page-select-wrapper {
  width: 70px;
  height: 26px;
  display: flex;
  align-items: center;
  overflow: hidden;
  border-radius: 4px;
  border: 1px solid var(--v-extraGrey-base, #ccc);
}

.pagination-select {
  font-size: 13px;
}

.pagination-select >>> .v-input__control,
.pagination-select >>> .v-input__slot {
  min-height: 26px !important;
  height: 26px !important;
  padding: 0 4px !important;
  margin: 0 !important;
  box-shadow: none !important;
  background-color: transparent !important;
}

.pagination-select >>> .v-select__selections {
  padding: 0 !important;
  min-height: 26px !important;
  line-height: 26px !important;
}

.pagination-select >>> input {
  padding: 0 !important;
  margin: 0 !important;
  text-align: center;
  height: 26px !important;
  max-height: 26px !important;
}

.pagination-select >>> .v-input__append-inner {
  margin-top: 0 !important;
  align-items: center !important;
  height: 26px !important;
  width: 16px !important;
}

.pagination-select >>> .v-input__append-inner .v-icon {
  font-size: 16px !important;
}

.paginationBox {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.title {
  padding: 0px 20px;
}

@media (max-width: 960px) {
  .paginationBox {
    padding-bottom: 50px;
  }
}

.action {
  display: inline-flex;
  align-items: center;
}

.primaryText--text {
  font-size: 12px;
  font-weight: 500;
}

.action {
  display: flex;
  align-items: center;
}
</style>

<style>
.plant-datatable {
  max-height: 600px;
  overflow: auto;
  display: flex;
}

.theme--dark .plant-datatable th {
  z-index: 1;
  position: sticky;
  top: 0;
}

.theme--light .plant-datatable th {
  z-index: 1;
  position: sticky;
  top: 0;
}

.blur-background {
  filter: blur(1px);
  transition: filter 0.3s ease;
}
.location {
  width: 15px;
  height: 15px;
  padding-left: 5px;
}

.mapButton {
  text-transform: none !important;
  font-size: 14px !important;
  color: var(--v-extraGrey-base);
  border-radius: 5px;
  border: 1px solid #213974 !important;
  height: 35px;
}

.theme--dark .mapButton {
  background-color: var(--v-primary-base) !important;
}

.theme--light .mapButton {
  background-color: var(--v-primary-base) !important;
}

.legend-background span {
  color: var(--v-extraGrey-base);
  font-size: 14px;
  font-weight: 500;
}

@media (max-width: 510px) {
  .legend-background span {
    font-size: 12px;
  }
}
</style>
